'use client';

import type { FC } from 'react';
import { forwardRef } from 'react';
import { cn } from '../../utils/cn';
import { useHaptic } from '../../hooks/useHaptic';

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  className?: string;
}

export const Switch: FC<SwitchProps> = forwardRef<HTMLInputElement, SwitchProps>(
  ({ checked, defaultChecked, disabled, onChange, className }, ref) => {
    const { impact } = useHaptic();

    // The tap on the track is answered by the tint; the haptic answers the value settling,
    // so it fires on change, not on touch. `impact` and not `selectionChanged`: the latter
    // is documented for movement through a series of discrete values, and a binary switch
    // fixes one value instead of travelling between them.
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      impact('light');
      onChange?.(e.target.checked);
    };

    return (
      <label className={cn('group relative inline-block w-11 h-7 cursor-pointer', className)}>
        <input
          ref={ref}
          type="checkbox"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={handleChange}
          className="peer opacity-0 w-0 h-0"
        />
        <span
          className={cn(
            'absolute cursor-pointer inset-0 bg-[#ccc] transition-all duration-[0.4s] rounded-full',
            // The knob rests at the inline start and travels to the inline end when on, so
            // mirrored it starts on the right and moves left — a switch that still slid
            // rightwards would read as the one untranslated control on the screen.
            'before:absolute before:content-[\'\'] before:h-5 before:w-5 before:start-1 before:bottom-1 before:bg-white before:transition-all before:duration-[0.4s] before:rounded-full',
            'peer-checked:bg-blue-500 peer-focus:shadow-[0_0_1px_#3b82f6] peer-checked:before:translate-x-4 rtl:peer-checked:before:-translate-x-4 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed',
            // Press step on the track. The knob is the ::before pseudo-element, so it paints
            // above the inset shadow and stays bright while the track tints under it.
            'group-active:shadow-[inset_0_0_0_9999px_rgba(255,255,255,0.08)] peer-disabled:group-active:shadow-none',
          )}
        />
      </label>
    );
  },
);

Switch.displayName = 'Switch';
